'use client';

import Image from 'next/image';
import rewardImg from '@/features/reward/assets/icon-reward.png';
import { useRewardCountManager } from '@/features/reward/store/reward-count';
import useFn from '@/hooks/use-fn';
import { useAtomValue } from 'jotai';
import { isLoginAtom } from '@/store';
import { commonPage } from '@/const/page-url';
import { Link } from '@/i18n/navigation';
import { useGetRewardCenterCount } from '@/features/reward/services/rewardcenter';
import { useGetBagCount } from '@/features/reward/services/backpack';

import { useEffect, useMemo, useState } from 'react';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import LottieView from '@/utils/lottie-helper';
import rewardJson from '@/features/home/lottie/reward.json';
import { closeUserCenter } from '@/features/user-center';
import { isTouchSupported } from '@/utils/node';
import { useRouter } from '@/i18n/navigation';
import { useChangeSubscription } from '@/hooks/use-change-subscription';
import { showCouponDrawer } from '@/features/reward/modules/coupon-drawer';
import { track } from '@/utils/track';
import { useBagCountManager } from '@/features/reward/store/bag-count';

export default function RewardEntry() {
  const isLogin = useAtomValue(isLoginAtom);
  const router = useRouter();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const { count, setCount } = useRewardCountManager();
  const { bagCount, setBagCount } = useBagCountManager();

  // 获取奖励中心计数（改为手动触发模式）
  // Get reward center count (changed to manual trigger mode)
  const { data, refetch } = useGetRewardCenterCount.useQueryHook({
    queryConfig: {
      enabled: false, // 改为手动触发
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: true,
    },
  });

  // 获取背包计数（改为手动触发模式）
  // Get backpack count (changed to manual trigger mode)
  const { data: backpackData, refetch: backpackRefetch } = useGetBagCount.useQueryHook({
    queryConfig: {
      enabled: false, // 改为手动触发
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: true,
    },
  });

  // 初始加载（登录时）
  // Initial load (when logged in)
  useEffect(() => {
    if (isLogin) {
      refetch();
      backpackRefetch();
    }
  }, [isLogin, refetch]);

  // 订阅 reward 变化（通过统一的 change-check 轮询）
  // Subscribe to reward changes (through unified change-check polling)
  useChangeSubscription('reward', refetch, isLogin);
  // 订阅 backpack 变化（通过统一的 change-check 轮询）
  // Subscribe to backpack changes (through unified change-check polling)
  useChangeSubscription('backpack', backpackRefetch, isLogin);

  const { playAudio } = useAudioHelper({ id: AudioId.ButtonClick });
  const handleClick = useFn((e?: React.MouseEvent | React.TouchEvent) => {
    if (!isLogin) {
      return;
    }
    closeUserCenter();
    console.log('Reward entry clicked', e); // 调试用
    playAudio();
    showCouponDrawer();
    // 增加埋点
    track('reward_entrance_click');
  });

  useEffect(() => {
    setCount(data?.count ?? 0);
  }, [data, setCount]);

  useEffect(() => {
    setBagCount(backpackData?.bag_count ?? 0);
  }, [backpackData, setBagCount]);

  // 计算总数量: 奖励中心 + 背包
  const allCount = useMemo(() => count + bagCount, [count, bagCount]);
  const allCountDisplay = useMemo(() => allCount > 99 ? '99+' : allCount, [allCount]);

  const handleComplete = () => {
    setIsLoaded(true);
  };

  const handlePointerDown = () => {
    setIsActive(true);
  };

  const handlePointerUp = () => {
    setIsActive(false);
  };

  useEffect(() => {
    router.prefetch(commonPage.reward);
  }, [router]);

  return (
    <div
      className={`!ml-[6px] relative flex items-end transition-transform duration-150 cursor-pointer select-none ${isActive ? 'scale-90' : 'scale-100'}`}
      // onClick={(e) => handleClick(e)}
      onMouseDown={handlePointerDown}
      // onMouseUp={handlePointerUp}
      onMouseUp={(e) => {
        const isTouch = isTouchSupported();
        if (!isTouch) {
          handlePointerUp();
          handleClick(e);
        }
      }}
      // onMouseLeave={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchEnd={(e) => {
        const isTouch = isTouchSupported();
        if (isTouch) {
          handlePointerUp();
          handleClick(e);
        }
      }}
    >
      {isLogin ? <>
        {
          allCount > 0 ?
            <div className='relative pointer-events-none w-[43px] h-[35px]'>
              {
                Boolean(allCount) && (
                  <div className="absolute z-10 top-[-2px] right-[-2px] px-[3px] min-w-[11px] h-[11px] flex items-center justify-center bg-accent-red text-white text-[9px] rounded-full pointer-events-none">{allCountDisplay}</div>
                )
              }
              <LottieView onLoadedImages={handleComplete} className='absolute w-[43px] h-[48px] top-[48%] -translate-y-1/2' animationData={rewardJson} loop={true} />
            </div>
            : <Image src={rewardImg} alt="reward" width={35} height={35} className='pointer-events-none' priority></Image>
        }
      </> : (
        <Link href={commonPage.reward} onClick={(e) => e.stopPropagation()}>
          <Image src={rewardImg} alt="reward" width={35} height={35} className='pointer-events-none' priority></Image>
        </Link>
      )}
      {
        allCount > 0 && !isLoaded ? <Image src={rewardImg} className='absolute top-0 left-0 z-[-1] pointer-events-none' alt="reward" width={35} height={35} priority></Image>
          : null
      }
    </div>
  );
}